import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import storageService from '../../services/storageService';
import { updateAccountDetails } from '../../services/userService';

const ProcurementClientSettingsPanel: React.FC = () => {
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();
  const isLegacyAdmin = false;

  const [hideBillingFromUsers, setHideBillingFromUsers] = useState(
    account?.clientSettings?.hideBillingFromUsers ?? true
  );

  const [enableProcurementModule, setEnableProcurementModule] = useState<boolean>(
    Boolean(account?.clientSettings?.enableProcurementModule)
  );

  const [enableBiddingModule, setEnableBiddingModule] = useState<boolean>(
    account?.clientSettings?.enableBiddingModule !== false
  );

  // Brand Identity State
  const [primaryColor, setPrimaryColor] = useState(account?.brand_identity?.primary_color ?? '');
  const [secondaryColor, setSecondaryColor] = useState(
    account?.brand_identity?.secondary_color ?? ''
  );
  const [fontFamily, setFontFamily] = useState(account?.brand_identity?.font_family ?? '');
  const [logoStoragePath, setLogoStoragePath] = useState(
    account?.brand_identity?.logo_storage_path ?? ''
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadLogoUrl = async () => {
      if (logoStoragePath) {
        try {
          const result = await storageService.getDownloadURL(logoStoragePath);
          if (result.success && isMounted) {
            setLogoUrl(result.url);
          }
        } catch (_error) {
          // ignore error
        }
      } else if (isMounted) {
        setLogoUrl(null);
      }
    };
    loadLogoUrl();
    return () => {
      isMounted = false;
    };
  }, [logoStoragePath]);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !account?.clientId) {
      return;
    }

    setIsUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const storagePath = `clients/${account.clientId}/brand_assets/logo.${ext}`;
      const uploadResult = await storageService.uploadFile(storagePath, file);
      if (uploadResult.success) {
        setLogoStoragePath(uploadResult.path);
        const result = await storageService.getDownloadURL(uploadResult.path);
        if (result.success) {
          const cacheBuster = `&t=${Date.now()}`;
          setLogoUrl(result.url + cacheBuster);
        }
        showSnackbar('Logo uploaded successfully', 'success');
      } else {
        throw new Error('Upload failed');
      }
    } catch (_err) {
      showSnackbar('Failed to upload logo', 'error');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    try {
      const clientPayload: Record<string, unknown> = {
        brand_identity: {
          primary_color: primaryColor,
          secondary_color: secondaryColor,
          font_family: fontFamily,
          logo_storage_path: logoStoragePath,
        },
        clientSettings: {
          ...account?.clientSettings,
          hideBillingFromUsers,
          enableProcurementModule,
          enableBiddingModule,
        },
      };

      await updateAccountDetails({
        client: clientPayload,
      });

      const moduleToggled =
        Boolean(enableBiddingModule) !== (account?.clientSettings?.enableBiddingModule !== false);

      if (moduleToggled) {
        await refreshUserDetails();
      }

      showSnackbar('Client settings saved successfully', 'success');
    } catch (_err) {
      showSnackbar('Failed to save client settings', 'error');
    }
  };

  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  });

  const isInitialMount = useRef(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      handleSaveRef.current();
    }, 1500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Organization Settings
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Feature Flags
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={hideBillingFromUsers}
              onChange={(e) => setHideBillingFromUsers(e.target.checked)}
              name="hideBillingFromUsers"
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Hide Billing from Users</Typography>
              <Typography variant="body2" color="text.secondary">
                Hides the estimated cost chips and billing details from all users in your
                organization.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        {isLegacyAdmin && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
              Platform Ecosystems
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableProcurementModule}
                    onChange={(e) => setEnableProcurementModule(e.target.checked)}
                    name="enableProcurementModule"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Enable Procurement Module (Buy-Side)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allows this client to use the Procurement and Supplier Evaluation workspace.
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: 'flex-start' }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={enableBiddingModule}
                    onChange={(e) => setEnableBiddingModule(e.target.checked)}
                    name="enableBiddingModule"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Enable Bidding Module (Sell-Side)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allows this client to use the core Tender/Proposal Writing workspace. Warning:
                      Enabling this will unlock the full suite of bidding features and tools.
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: 'flex-start' }}
              />
            </FormGroup>
          </>
        )}

        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
          Brand Identity
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Primary Brand Color (Hex)"
              variant="outlined"
              fullWidth
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#FFFFFF"
              slotProps={{
                input: {
                  startAdornment: (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        bgcolor: primaryColor || 'transparent',
                        border: '1px solid #ccc',
                        mr: 1,
                      }}
                    />
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Secondary Brand Color (Hex)"
              variant="outlined"
              fullWidth
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.value)}
              placeholder="#000000"
              slotProps={{
                input: {
                  startAdornment: (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        bgcolor: secondaryColor || 'transparent',
                        border: '1px solid #ccc',
                        mr: 1,
                      }}
                    />
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              freeSolo
              options={[
                'Arial',
                'Helvetica',
                'Times New Roman',
                'Courier New',
                'Roboto',
                'Open Sans',
                'Lato',
                'Montserrat',
                'Inter',
                'Poppins',
              ]}
              value={fontFamily}
              onInputChange={(_, newValue) => setFontFamily(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Brand Font Family" variant="outlined" fullWidth />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
              <Button variant="outlined" component="label" disabled={isUploadingLogo}>
                {isUploadingLogo ? <CircularProgress size={24} /> : 'Upload Logo'}
                <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
              </Button>
              {logoUrl && (
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    border: '1px solid #ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    bgcolor: '#f5f5f5',
                  }}
                >
                  <img
                    src={logoUrl}
                    alt="Brand Logo Preview"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </Box>
              )}
              {logoStoragePath && !logoUrl && (
                <Typography variant="body2" color="success.main" noWrap>
                  Logo uploaded successfully
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ProcurementClientSettingsPanel;
